import { schema, t, type SchemaType } from "@colyseus/schema";

/**
 * Button bits carried by every input frame. A bit means "pressed in this
 * step" (the client also keeps a short buffer, see the client controller),
 * never "held".
 */
export const Button = {
  Jump: 1 << 0,
  Dash: 1 << 1,
  Light: 1 << 2,
  /** B: pick up / throw an object (it was the heavy attack until D39). */
  Grab: 1 << 3,
  Special1: 1 << 4,
  Special2: 1 << 5,
  Slam: 1 << 6,
} as const;
export type ButtonName = keyof typeof Button;
export const ALL_BUTTONS = 0x7f;

/** Movement axes travel as integers in [-AXIS_MAX, AXIS_MAX]. */
export const AXIS_MAX = 100;

/**
 * One fixed-step input frame (client → server). The client sends an intention
 * — a world-space direction and button presses — never positions or scores.
 */
export const PlayerInput = schema(
  {
    mx: t.int8().default(0),
    mz: t.int8().default(0),
    buttons: t.uint8().default(0),
    target: t.uint8().default(0),
    targetX: t.float32().default(0),
    targetZ: t.float32().default(0),
  },
  "PlayerInput",
);
export type PlayerInput = SchemaType<typeof PlayerInput>;

/** What the simulation reads from an input; satisfied by `PlayerInput`. */
export interface InputCommand {
  readonly mx: number;
  readonly mz: number;
  readonly buttons: number;
  readonly target?:number;
  readonly targetX?:number;
  readonly targetZ?:number;
}

export const NEUTRAL_INPUT: InputCommand = Object.freeze({ mx: 0, mz: 0, buttons: 0 });

/** Server-side value-domain clamps for `defineInput({ sanitize })`. */
export const INPUT_SANITIZE = {
  mx: [-AXIS_MAX, AXIS_MAX],
  mz: [-AXIS_MAX, AXIS_MAX],
  buttons: [0, ALL_BUTTONS],
  target:[0,1],
  targetX:[-40,40],
  targetZ:[-30,30],
} as const;

export function quantizeAxis(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const clamped = Math.max(-1, Math.min(1, value));
  return Math.round(clamped * AXIS_MAX) | 0;
}
